// Genre detection and variety utilities
// Shared between AnalyticsPage and EventManagePage

import { SongRequest } from '../services/requests';

// Simple artist-to-genre mapping for demo purposes
// In production, this would come from iTunes API genre data
export const ARTIST_GENRE_MAP: Record<string, string> = {
  // Pop
  'taylor swift': 'Pop',
  'ed sheeran': 'Pop',
  'dua lipa': 'Pop',
  'justin timberlake': 'Pop',
  'pharrell williams': 'Pop',
  'the weeknd': 'Pop',
  'bts': 'K-Pop',
  'ariana grande': 'Pop',
  'billie eilish': 'Pop',
  'bruno mars': 'Pop',
  'miley cyrus': 'Pop',
  'olivia rodrigo': 'Pop',
  'harry styles': 'Pop',
  'justin bieber': 'Pop',
  'selena gomez': 'Pop',
  'lizzo': 'Pop',
  'lady gaga': 'Pop',
  'katy perry': 'Pop',
  'adele': 'Pop',
  // Rock
  'queen': 'Rock',
  'ac/dc': 'Rock',
  'led zeppelin': 'Rock',
  'nirvana': 'Rock',
  'foo fighters': 'Rock',
  'guns n roses': 'Rock',
  'metallica': 'Rock',
  'the beatles': 'Rock',
  'green day': 'Rock',
  'linkin park': 'Rock',
  'eagles': 'Rock',
  // Hip-Hop/Rap
  'drake': 'Hip-Hop',
  'kendrick lamar': 'Hip-Hop',
  'eminem': 'Hip-Hop',
  'travis scott': 'Hip-Hop',
  'post malone': 'Hip-Hop',
  'lil nas x': 'Hip-Hop',
  'cardi b': 'Hip-Hop',
  'megan thee stallion': 'Hip-Hop',
  'kanye west': 'Hip-Hop',
  'jay-z': 'Hip-Hop',
  '21 savage': 'Hip-Hop',
  'the kid laroi': 'Hip-Hop',
  // Electronic/Dance
  'daft punk': 'Electronic',
  'calvin harris': 'Electronic',
  'marshmello': 'Electronic',
  'tones and i': 'Dance',
  'avicii': 'Electronic',
  'deadmau5': 'Electronic',
  'david guetta': 'Electronic',
  'tiesto': 'Electronic',
  'glass animals': 'Electronic',
  // R&B
  'beyonce': 'R&B',
  'usher': 'R&B',
  'sza': 'R&B',
  // Note: 'the weeknd' already listed in Pop section above
  'frank ocean': 'R&B',
  'daniel caesar': 'R&B',
  'giveon': 'R&B',
  // Country
  'luke bryan': 'Country',
  'carrie underwood': 'Country',
  'morgan wallen': 'Country',
  'blake shelton': 'Country',
  'luke combs': 'Country',
  'dolly parton': 'Country',
  'kenny chesney': 'Country',
};

// Color palette for genres
export const GENRE_COLORS: Record<string, string> = {
  'Pop': '#6366F1', // Indigo
  'K-Pop': '#EC4899', // Pink
  'Rock': '#EF4444', // Red
  'Hip-Hop': '#F59E0B', // Amber
  'Electronic': '#8B5CF6', // Purple
  'Dance': '#10B981', // Green
  'R&B': '#3B82F6', // Blue
  'Country': '#F97316', // Orange
  'Other': '#6B7280', // Gray
};

// All available genres for suggestions
export const ALL_GENRES = ['Pop', 'Rock', 'Hip-Hop', 'Electronic', 'Dance', 'R&B', 'Country', 'K-Pop'];

// Infer genre from artist name
export function inferGenre(artist: string): string {
  const normalizedArtist = artist.toLowerCase().trim();
  // Check for exact match first
  if (ARTIST_GENRE_MAP[normalizedArtist]) {
    return ARTIST_GENRE_MAP[normalizedArtist];
  }
  // Check for partial match (e.g., "Taylor Swift" in "Taylor Swift ft. Ed Sheeran")
  for (const [key, genre] of Object.entries(ARTIST_GENRE_MAP)) {
    if (normalizedArtist.includes(key)) {
      return genre;
    }
  }
  return 'Other';
}

// Calculate genre distribution from requests
export function calculateGenreDistribution(requests: SongRequest[]): Record<string, number> {
  return requests.reduce((acc, r) => {
    const genre = inferGenre(r.song.artist);
    acc[genre] = (acc[genre] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

// Check if queue is too homogeneous (one genre > threshold %)
// Returns the dominant genre and its percentage, or null if diverse enough
export function checkQueueVariety(
  requests: SongRequest[],
  minRequestsToCheck: number = 5,
  homogeneousThreshold: number = 70 // percentage
): { isDominant: boolean; dominantGenre: string; percentage: number; missingGenres: string[] } | null {
  if (requests.length < minRequestsToCheck) {
    return null; // Not enough requests to assess variety
  }

  const genreDistribution = calculateGenreDistribution(requests);
  const totalRequests = requests.length;

  // Find the dominant genre
  let dominantGenre = 'Other';
  let maxCount = 0;

  for (const [genre, count] of Object.entries(genreDistribution)) {
    if (count > maxCount) {
      maxCount = count;
      dominantGenre = genre;
    }
  }

  const percentage = Math.round((maxCount / totalRequests) * 100);
  const isDominant = percentage >= homogeneousThreshold;

  // Find genres that are missing from the queue
  const presentGenres = new Set(Object.keys(genreDistribution).filter(g => g !== 'Other'));
  const missingGenres = ALL_GENRES.filter(g => !presentGenres.has(g));

  return {
    isDominant,
    dominantGenre,
    percentage,
    missingGenres,
  };
}

// Get suggested genres to diversify the queue
export function getSuggestedGenres(currentGenres: Record<string, number>, limit: number = 3): string[] {
  const presentGenres = new Set(Object.keys(currentGenres).filter(g => g !== 'Other'));

  // Prioritize genres that are completely missing
  const missingGenres = ALL_GENRES.filter(g => !presentGenres.has(g));

  // If we have missing genres, suggest those
  if (missingGenres.length > 0) {
    return missingGenres.slice(0, limit);
  }

  // Otherwise, suggest genres that have lower representation
  const genresByCount = Object.entries(currentGenres)
    .filter(([genre]) => genre !== 'Other')
    .sort((a, b) => a[1] - b[1])
    .map(([genre]) => genre);

  return genresByCount.slice(0, limit);
}
